
import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { useAppointments } from 'features/appointment/hooks/useAppointments';
import { usePrefetchAppointments } from 'features/appointment/hooks/usePrefetchAppointments';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateTime } from 'utils/dateFormatter';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import appointmentServices  from 'features/appointment/services/appointment.service';

const Appointment = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { prefetchNextPage, prefetchAppointment } =
    usePrefetchAppointments();

  // URL params
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialLimit = parseInt(searchParams.get('limit')) || 10;
  const initialStatus = searchParams.get('status') || '';
  const initialType = searchParams.get('type') || '';

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [status, setStatus] = useState(initialStatus);
  const [type, setType] = useState(initialType);

  const initialSortBy =
  searchParams.get('sortBy') || 'scheduledDate';

const initialSortOrder =
  searchParams.get('sortOrder') || 'desc';

const [sortBy, setSortBy] =
  useState(initialSortBy);

const [sortOrder, setSortOrder] =
  useState(initialSortOrder);

  // Filters
  const filters = {
    page,
    limit,
    status: status || undefined,
    type: type || undefined,
    sortBy,
  sortOrder,
  };

  // Fetch
  const { data: response, isLoading, isError, error } =
    useAppointments(filters);

    const { data: profileData } = useUserProfile();
    const user = profileData?.data;
    
    const restrictedRoles = [
      "delivery_manager",
      "travel_manager",
      // 'operations_manager',
      "category_manager",
      "finance_manager",
    ];
    
    const showPermissionError = restrictedRoles.includes(
      user?.role?.name
    );
    

  const appointments = response?.data?.appointments || [];
  const pagination = response?.data?.pagination || {};

const handleExport =
  async () => {
    try {
      const response =
        await appointmentServices.exportAppointments(
          {
            status,
            type,
             sortBy,
  sortOrder,
          }
        );

      const blob =
        new Blob(
          [response.data],
          {
            type: 'text/csv',
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          'a'
        );

      link.href = url;

      link.download = `appointments-${Date.now()}.csv`;

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );
    } catch (error) {
      console.error(
        'Failed to export appointments',
        error
      );
    }
  };

  // Sync URL
  useEffect(() => {
    const params = new URLSearchParams();

    params.set('page', page);
    params.set('limit', limit);

    if (status) params.set('status', status);
    if (type) params.set('type', type);

    if (sortBy) params.set('sortBy', sortBy);
if (sortOrder)
  params.set('sortOrder', sortOrder);

    setSearchParams(params);
  }, [page, limit, status, type, sortBy,
  sortOrder, setSearchParams]);

  // Prefetch next page
  useEffect(() => {
    if (pagination?.totalPages) {
      const { page: currentPage, totalPages } = pagination;

      if (currentPage < totalPages) {
        prefetchNextPage(filters, totalPages);
      }
    }
  }, [pagination, filters]);

  const handleViewClick = (appointment) => {
    navigate(`/appointments/${appointment._id}`);
  };

  const handleFilterChange = (filterType, value) => {
    setPage(1);

    if (filterType === 'status') setStatus(value);
    if (filterType === 'type') setType(value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Status colors
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-600',
      confirmed: 'bg-blue-500/10 text-blue-600',
      completed: 'bg-green-500/10 text-green-600',
      cancelled: 'bg-red-500/10 text-red-600',
      rescheduled: 'bg-orange-500/10 text-orange-600',
      rejected: 'bg-gray-500/10 text-gray-600',
      no_show: 'bg-red-700/10 text-red-700',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600';
  };


  const handleSort = (column) => {
  const newOrder =
    sortBy === column &&
    sortOrder === 'asc'
      ? 'desc'
      : 'asc';

  setSortBy(column);
  setSortOrder(newOrder);
  setPage(1);
};

const SortIcon = ({ column }) => {
  if (sortBy !== column) {
    return (
      <span className="text-gray-400">
        ↕
      </span>
    );
  }

  return sortOrder === 'asc'
    ? <span>↑</span>
    : <span>↓</span>;
};



  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>
            Error loading appointments
          </div>
          <p className='text-muted-foreground'>
            {error?.message || 'Something went wrong'}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`p-6 space-y-6 ${PAGE_CONTENT_WIDTH}`}>
        {/* Header */}
        <PageHeader
          icon={Calendar}
          title='Appointments'
          subtitle='Manage all Property Appointments'
          actions={
            !showPermissionError && (
              <button
                onClick={handleExport}
                className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
              >
                <Download size={16} />
                <span>Export</span>
              </button>
            )
          }
        />

        {/* Filters */}
        {/* <div className='bg-card border rounded-lg p-4'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        
            <select
              value={status}
              onChange={(e) =>
                handleFilterChange('status', e.target.value)
              }
              className='border p-2 rounded'
            >
              <option value=''>All Status</option>
              <option value='pending'>Pending</option>
              <option value='confirmed'>Confirmed</option>
              <option value='completed'>Completed</option>
              <option value='cancelled'>Cancelled</option>
              <option value='rescheduled'>Rescheduled</option>
              <option value='rejected'>Rejected</option>
              <option value='no_show'>No Show</option>
            </select>


            <select
              value={type}
              onChange={(e) =>
                handleFilterChange('type', e.target.value)
              }
              className='border p-2 rounded'
            >
              <option value=''>All Types</option>
              <option value='property'>Property</option>
              <option value='tour'>Tour</option>
            </select>


            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setPage(1);
              }}
              className='border p-2 rounded'
            >
              <option value='5'>5</option>
              <option value='10'>10</option>
              <option value='20'>20</option>
            </select>
          </div>
        </div> */}

        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>

            {/* Status Filter */}
            <div>
              <label className='block text-sm font-medium mb-2'>
                Status
              </label>
              <select
                value={status}
                onChange={(e) =>
                  handleFilterChange('status', e.target.value)
                }
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Status</option>
                <option value='pending'>Pending</option>
                <option value='confirmed'>Confirmed</option>
                <option value='completed'>Completed</option>
                <option value='cancelled'>Cancelled</option>
                <option value='rescheduled'>Rescheduled</option>
                <option value='rejected'>Rejected</option>
                <option value='no_show'>No Show</option>
              </select>
            </div>

            {/* Type Filter */}
            {/* <div>
              <label className='block text-sm font-medium mb-2'>
                Type
              </label>
              <select
                value={type}
                onChange={(e) =>
                  handleFilterChange('type', e.target.value)
                }
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Types</option>
                <option value='property'>Property</option>
                <option value='tour'>Tour</option>
              </select>
            </div> */}

            {/* Items Per Page */}
            <div>
              <label className='block text-sm font-medium mb-2'>
                Items Per Page
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setPage(1);
                }}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value='5'>5</option>
                <option value='10'>10</option>
                <option value='20'>20</option>
              </select>
            </div>

          </div>
        </div>

        {/* Table */}
        <div className='border rounded-lg overflow-hidden'>
          <table className='w-full'>
            <thead className='bg-muted'>
              <tr>
                {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Appointment #</th> */}
                <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("createdAt")}
>
  <div className="flex items-center gap-1">
    Appointment #
    <SortIcon column="createdAt" />
  </div>
</th>
                <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Customer</th>
                <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Seller</th>
                <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Type</th>
                {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Booking</th> */}
                {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Scheduled</th> */}
                <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("scheduledDate")}
>
  <div className="flex items-center gap-1">
    Scheduled
    <SortIcon column="scheduledDate" />
  </div>
</th>
                {/* <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Status</th> */}
                <th
  className="px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer"
  onClick={() => handleSort("scheduledDate")}
>
  <div className="flex items-center gap-1">
    Scheduled
    <SortIcon column="scheduledDate" />
  </div>
</th>
                <th className='px-4 py-5 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>Action</th>
              </tr>
            </thead>

            <tbody>
              {showPermissionError ? (
    <tr>
      <td
        colSpan='11'
        className='px-4 py-8 text-center text-destructive'
      >
        Error loading appointments: You do not have permission to perform this action!
      </td>
    </tr>
  ) : isLoading ? (
                <tr>
                  <td colSpan='8' className='text-center p-6'>
                    Loading...
                  </td>
                </tr>
              ) : appointments.length === 0 ? (
                <tr>
                  <td colSpan='8' className='text-center p-6'>
                    No appointments found
                  </td>
                </tr>
              ) : (
                appointments.map((item) => (
                  <tr key={item._id} className='border-t'>
                    <td className='px-4 py-3'>
                      <span className='text-sm font-medium text-primary'>
                      {item.appointmentNumber}
                      </span>
                    </td>

                     <td className='px-4 py-3'>
                        <div className='flex items-center space-x-3'>
                          {item.customerId?.profilePhoto ? (
                            <img
                              src={item.customerId.profilePhoto}
                              alt='Customer'
                              className='w-8 h-8 rounded-full object-cover'
                            />
                          ) : (
                            <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center'>
                              <User size={16} />
                            </div>
                          )}
                          <div>
                            <p className='text-sm font-medium whitespace-nowrap'>
                              {item.customerId?.phoneNumber || '--'}
                            </p>
                            <p className='text-xs text-muted-foreground'>
                              Customer
                            </p>
                          </div>
                        </div>
                      </td>

                    <td className='px-4 py-3'>
                        <div className='flex items-center space-x-3'>
                          {item.sellerId?.profilePhoto ? (
                            <img
                              src={item.sellerId.profilePhoto}
                              alt='Seller'
                              className='w-8 h-8 rounded-full object-cover flex-shrink-0'
                            />
                          ) : (
                            <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center'>
                              <User size={16} />
                            </div>
                          )}
                          <div>
                            <p className='text-sm font-medium whitespace-nowrap'>
                              {item.sellerId?.phoneNumber || '--'}
                            </p>
                            <p className='text-xs text-muted-foreground'>
                              Seller
                            </p>
                          </div>
                        </div>
                      </td>

                    <td className='px-4 py-3 capitalize'>
                      {item.type || '--'}
                    </td>

                    {/* <td className='px-4 py-3 capitalize'>
                      {item.booking_type || '--'}
                    </td> */}

                    <td className='px-4 py-3'>
                      <div>{formatDateTime(item.scheduledDate)}</div>
                      <div className='text-xs text-muted-foreground'>
                        {item.scheduledTime || '--'}
                      </div>
                    </td>

                    {/* <td className='px-4 py-3'>
                      <span
                        className={`px-2 py-1 text-xs rounded ${getStatusColor(
                          item.status
                        )}`}
                      >
                        {item.status}
                      </span>
                    </td> */}

                    <td className='px-4 py-3'>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            item.status,
                          )}`}
                        >
                          {item.status
                            ?.replace(/_/g, ' ')
                            ?.toUpperCase()}
                        </span>
                      </td>

                    {/* <td className='px-4 py-3'>
                      <button
                        onClick={() => handleViewClick(item)}
                        onMouseEnter={() =>
                          prefetchAppointment(item._id)
                        }
                        className='flex items-center gap-1 text-primary'
                      >
                        <Eye size={14} />
                        View
                      </button>
                    </td> */}

                    <td className='px-4 py-3'>
                      <button
                        onClick={() => handleViewClick(item)}
                        onMouseEnter={() =>
                          prefetchAppointment(item._id)
                        }
                        className='inline-flex items-center px-3 py-1 text-sm bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors'
                      >
                        <Eye size={14} className='mr-1' />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {/* {pagination.totalPages > 1 && (
            <div className='flex justify-between p-4 border-t'>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft />
              </button>

              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pagination.totalPages}
              >
                <ChevronRight />
              </button>
            </div>
          )} */}

          {!showPermissionError && pagination.totalPages > 1 && (
  <div className='px-4 py-3 border-t border-border flex items-center justify-between'>
    
    {/* LEFT: Showing info */}
    <div className='text-sm text-muted-foreground'>
      Showing{' '}
      {(pagination.page - 1) * limit + 1} to{' '}
      {Math.min(pagination.page * limit, pagination.total)} of{' '}
      {pagination.total} results
    </div>

    {/* RIGHT: Controls */}
    <div className='flex items-center space-x-2'>
      
      {/* Previous */}
      <button
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1}
        className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
      >
        <ChevronLeft size={16} />
      </button>

      {/* Page Numbers */}
      <div className='flex items-center space-x-1'>
        {[...Array(pagination.totalPages)].map((_, idx) => {
          const pageNum = idx + 1;
          const current = pagination.page;

          if (
            pageNum === 1 ||
            pageNum === pagination.totalPages ||
            (pageNum >= current - 1 && pageNum <= current + 1)
          ) {
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                  pageNum === current
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
                }`}
              >
                {pageNum}
              </button>
            );
          } else if (
            pageNum === current - 2 ||
            pageNum === current + 2
          ) {
            return (
              <span key={pageNum} className='text-muted-foreground'>
                ...
              </span>
            );
          }

          return null;
        })}
      </div>

      {/* Next */}
      <button
        onClick={() => handlePageChange(page + 1)}
        disabled={page === pagination.totalPages}
        className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
      >
        <ChevronRight size={16} />
      </button>

    </div>
  </div>
)}
        </div>
      </div>
    </Layout>
  );
};

export default Appointment;